import React from 'react';
import { useFormik, FormikProvider } from 'formik';
import { useMutation } from 'react-apollo';
import gql from 'graphql-tag';
import * as yup from 'yup';
import propTypes from 'prop-types';
import { isValidZipcode, isValidCNPJ } from '../../../utils/validators';
import runtime from '../../../utils/runtime';
import useToast from '../../../hooks/useToast';
import CondominiumRegister from '../../../components/CondominiumRegister';
import { makePhoneNumber } from '../../../utils/masks';
import useContext from '../../../hooks/useContext';

const createCondominiumGql = gql`
  mutation createCondominium($condominium: RegisterCondominium) {
    createCondominium(condominium: $condominium) {
      _id
      name
    }
  }
`;

const condominiumSchema = yup.object({
  name: yup.string().required(),
  belongsTo: yup
    .string()
    .required()
    .default(runtime.RAZZLE_PROJECT || 'kiper'),
  doc: yup
    .string()
    .test('doc-validation', 'CNPJ inválido', doc => isValidCNPJ(doc))
    .required(),
  phoneNumber: yup
    .string()
    .test({
      message: 'invalid-phone',
      // eslint-disable-next-line no-useless-escape
      test: a => a && a.match(/(\(\d{2}\)\s)(\d{4,5}\-\d{4})/gm),
    })
    .required(),
  country: yup.string().required(),
  totalUnits: yup
    .number()
    .min(0)
    .max(Number.MAX_SAFE_INTEGER)
    .required(),
  address: yup.object({
    zipcode: yup
      .string()
      .test('zipcode-validation', 'CEP inválido', zipcode => isValidZipcode(zipcode))
      .required(),
    number: yup
      .number()
      .min(0)
      .max(Number.MAX_SAFE_INTEGER)
      .required(),
  }),
});

export default function Put({ history }) {
  const toast = useToast();
  const { write } = useContext();
  const [createCondominiumrFn] = useMutation(createCondominiumGql, {
    onCompleted: async data => {
      write({
        condominiumId: data.createCondominium._id,
        callback: () => {
          toast.fire({
            icon: 'success',
            title: `Condomínio criado com sucesso`,
          });
          history.push('/dashboard');
        },
      });
    },
    onError: () =>
      toast.fire({
        icon: 'error',
        title: 'Problemas ao registrar. Tente novamente',
      }),
  });

  const formikBag = useFormik({
    initialValues: {
      condominium: {
        name: '',
        doc: '',
        belongsTo: runtime.RAZZLE_PROJECT || 'kiper',
        country: '+55',
        phoneNumber: '',
        totalUnits: '',
        address: {
          zipcode: '',
          number: '',
        },
      },
    },
    initialErrors: { user: {}, condominium: {} },
    validationSchema: yup.object({
      condominium: condominiumSchema,
    }),
    onSubmit: ({ condominium: _condominium }) => {
      const condominium = {
        ..._condominium,
        belongsTo: runtime.RAZZLE_PROJECT || 'kiper',
      };
      if (condominium.country && condominium.phoneNumber) {
        condominium.phoneNumber = makePhoneNumber({
          country: condominium.country,
          phone: condominium.phoneNumber,
        });
      }

      condominium.country = undefined;

      return createCondominiumrFn({ variables: { condominium } });
    },
  });

  return (
    <FormikProvider value={formikBag}>
      <CondominiumRegister header />
    </FormikProvider>
  );
}

Put.propTypes = {
  history: propTypes.object.isRequired,
};
